/**
 * BGG XML fields can be plain strings or { '#text': '...' } after fast-xml-parser.
 */
export const bggTextField = (field) => {
	if (field == null) return ''
	if (typeof field === 'string') return field
	if (typeof field === 'object' && field['#text'] != null) return String(field['#text'])
	return ''
}

/** Full cover (`image`) preferred; falls back to `thumbnail` from collection / thing XML. */
export const getBggGameCoverUrl = (game) => {
	if (!game) return ''
	const full = bggTextField(game.image).trim()
	const thumb = bggTextField(game.thumbnail).trim()
	return full || thumb || ''
}

const WSRV_IMAGE_PROXY = 'https://wsrv.nl/?url='

/**
 * BGG / geekdo image hosts typically omit CORS; html2canvas then leaves empty tiles.
 * wsrv.nl proxies the image and sends Access-Control-Allow-Origin: * so canvas export works.
 * @see https://wsrv.nl/docs
 */
export const getCanvasSafeCoverSrc = (game) => {
	let u = String(getBggGameCoverUrl(game) || '').trim()
	if (!u) return ''
	if (u.startsWith('//')) u = `https:${u}`
	if (!/^https?:\/\//i.test(u)) return u
	if (/\bwsrv\.nl\b/i.test(u) || /\bimages\.weserv\.nl\b/i.test(u)) return u
	return `${WSRV_IMAGE_PROXY}${encodeURIComponent(u)}`
}

/** Thumbnail only (for lightweight UI such as the picker roll strip). */
export const getBggGameThumbnailUrl = (game) => {
	if (!game) return ''
	return bggTextField(game.thumbnail).trim()
}

export const formatName = name => {
    if (name.slice(-1) === 's') {
        return `${name}'`
    }
    return `${name}'s`
}

export const filterPlayerCount = (collection, numPlayers, minPlayers, maxPlayers) => {
    return collection.filter(game => {
        if (minPlayers !== null && minPlayers !== 'any') {
            if (Number(game.stats['@_minplayers']) < Number(minPlayers)) {
                return false;
            }
        }

        if (maxPlayers !== null && maxPlayers !== 'any') {
            if (Number(game.stats['@_maxplayers']) > Number(maxPlayers)) {
                return false;
            }
        }

        if (numPlayers !== null && numPlayers !== 'any') {
            if (Number(game.stats['@_minplayers']) > Number(numPlayers) ||
                Number(game.stats['@_maxplayers']) < Number(numPlayers)) {
                return false;
            }
        }

        return true;
    })
}

/**
 * Uniform integer in [0, maxExclusive) using crypto.getRandomValues (rejection sampling).
 * Suitable for picking a winner index; avoids Math.random bias for this use case.
 */
export const randomInt = (maxExclusive) => {
	if (maxExclusive == null || maxExclusive <= 0) return 0
	const buf = new Uint32Array(1)
	const max = 0x100000000
	const limit = Math.floor(max / maxExclusive) * maxExclusive
	let x
	do {
		crypto.getRandomValues(buf)
		x = buf[0]
	} while (x >= limit)
	return x % maxExclusive
}

/**
 * Fisher–Yates shuffle on a shallow copy, then take first k items (unique subset when k <= length).
 */
export const pickRandomSubset = (collection, k) => {
	if (!collection || collection.length === 0) return []
	const n = collection.length
	const take = Math.min(k, n)
	const copy = collection.slice()
	for (let i = copy.length - 1; i > 0; i--) {
		const j = randomInt(i + 1)
		const t = copy[i]
		copy[i] = copy[j]
		copy[j] = t
	}
	return copy.slice(0, take)
}
